import { useState, useCallback } from 'react';
import React from 'react';

interface UnsavedChangesModalProps {
  isOpen: boolean;
  onSave: () => Promise<void>;
  onDiscard: () => void;
  onCancel: () => void;
}

const UnsavedChangesModal: React.FC<UnsavedChangesModalProps> = ({
  isOpen,
  onSave,
  onDiscard,
  onCancel
}) => {
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave();
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white p-6 rounded-xl shadow-xl max-w-md w-full mx-4">
        <h3 className="text-lg font-bold mb-2 text-gray-900">Unsaved Changes</h3>
        <p className="text-sm text-gray-700 mb-6">
          You have unsaved changes. What would you like to do?
        </p>
        <div className="flex flex-col sm:flex-row justify-end gap-3">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save & Leave'}
          </button>
          <button
            onClick={onDiscard}
            disabled={saving}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
          >
            Discard & Leave
          </button>
          <button
            onClick={onCancel}
            disabled={saving}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export const useUnsavedChanges = (
  hasUnsavedChanges: boolean,
  onSave: () => Promise<void>,
  onDiscard?: () => Promise<void>
) => {
  const [showModal, setShowModal] = useState(false);
  const [pendingDestination, setPendingDestination] = useState<string>('');
  const [navigationResolver, setNavigationResolver] = useState<((value: boolean) => void) | null>(null);

  const handleNavigationRequest = useCallback(async (destination: string): Promise<boolean> => {
    if (!hasUnsavedChanges) {
      return true; // Allow navigation
    }

    return new Promise((resolve) => {
      setPendingDestination(destination);
      setShowModal(true);
      setNavigationResolver(() => resolve);
    });
  }, [hasUnsavedChanges]);

  const handleSaveAndLeave = useCallback(async () => {
    try {
      await onSave();
      setShowModal(false);
      if (navigationResolver) {
        const resolver = navigationResolver;
        setNavigationResolver(null);
        resolver(true);
      }
    } catch (error) {
      console.error('Failed to save:', error);
      // Keep modal open on error
    }
  }, [onSave, navigationResolver]);

  const handleDiscardAndLeave = useCallback(async () => {
    try {
      if (onDiscard) {
        await onDiscard();
      }
      setShowModal(false);
      if (navigationResolver) {
        const resolver = navigationResolver;
        setNavigationResolver(null);
        resolver(true);
      }
    } catch (error) {
      console.error('Failed to discard changes:', error);
      // Close modal anyway but don't navigate
      setShowModal(false);
      if (navigationResolver) {
        const resolver = navigationResolver;
        setNavigationResolver(null);
        resolver(false);
      }
    }
  }, [navigationResolver, onDiscard]);

  const handleCancel = useCallback(() => {
    setShowModal(false);
    if (navigationResolver) {
      const resolver = navigationResolver;
      setNavigationResolver(null);
      resolver(false);
    }
  }, [navigationResolver]);

  const UnsavedChangesModalComponent = useCallback(() => (
    <UnsavedChangesModal
      isOpen={showModal}
      onSave={handleSaveAndLeave}
      onDiscard={handleDiscardAndLeave}
      onCancel={handleCancel}
    />
  ), [showModal, handleSaveAndLeave, handleDiscardAndLeave, handleCancel]);

  return {
    handleNavigationRequest,
    UnsavedChangesModal: UnsavedChangesModalComponent
  };
}; 